import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  TableContainer,
  Paper,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  CircularProgress,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  ViewButton,
  StyledButton,
  StyledAlert,
  StyledSnackbar,
  StyledAgGridContainer,
  inputStyle,
  StyledDialogBoxTitle,
} from "components/StyledComponents/styledComponents.js";
import { useNavigate } from "react-router-dom";
import useBusinessMonthYear from "components/GlobalHooks/useMonthlyBusiness";
import { writeFile, utils } from "xlsx";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import { monthEndStyles } from "components/Styles/monthEndStyles";
import { CustomPaper } from "components/Styles/globalStyles";
import URLs from "constants/urls";
import Cookie from "js-cookie";
import dayjs from "dayjs";

export default function MonthlyDSABusinessCard() {
  const { monthIdData, setAlert, handleClose } = useBusinessMonthYear();
  const [monthId, setMonthId] = useState(0);
  const businessCodeRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [servicesData, setServicesData] = useState([]);
  const MInput = inputStyle();
  const [openDailog, setOpenDailog] = useState(false);
  const [financeName, setFinanceName] = useState("");
  const jwt_token = Cookie.get("jwt_token");
  const [providerBusinessData, setProviderBusinessData] = useState([]);
  const currentMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
  const currentMonthEnd = dayjs().endOf("month").format("YYYY-MM-DD");
  const [rowData, setRowData] = useState([]);
  const [footerData, setFooterData] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const navigate = useNavigate();

  // ─── refs for both grids ────────────────────────────────────────────────────
  const mainGridRef = useRef(null);
  const dialogGridRef = useRef(null);

  // ─── Grid API state (fix for download + filter footer) ─────────────────────
  const [mainGridApi, setMainGridApi] = useState(null);
  const [dialogGridApi, setDialogGridApi] = useState(null);

  const [dialogFooter, setDialogFooter] = useState([]);

  // ─── compute pinned totals from any row array ───────────────────────────────
  const computeAndPinMain = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setFooterData([]);
      return;
    }
    const sum = (field) => rows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    setFooterData([
      {
        dsaName: "Total",
        totalDsaBusiness: sum("totalDsaBusiness"),
        totalGrossPayouts: sum("totalGrossPayouts"),
        totalFinalGrossPayouts: sum("totalFinalGrossPayouts"),
        totalDsaPayoutAmount: sum("totalDsaPayoutAmount"),
        totalFinalDsaPayoutsAmount: sum("totalFinalDsaPayoutsAmount"),
        totalSubDsaPayoutAmount: sum("totalSubDsaPayoutAmount"),
        totalFinalSubDsaPayoutsAmount: sum("totalFinalSubDsaPayoutsAmount"),
        dsaRevenue: sum("dsaRevenue"),
      },
    ]);
  }, []);

  const computeAndPinDialog = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      setDialogFooter([]);
      return;
    }
    const sum = (field) => rows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    setDialogFooter([
      {
        customerName: "Total",
        loanAmount: sum("loanAmount"),
        revenue: sum("revenue"),
        dsaPayoutValue: sum("dsaPayoutValue"),
        companyRetention: sum("companyRetention"),
        finalConnectorPayoutAmount: sum("finalConnectorPayoutAmount"),
        footerRow: true,
      },
    ]);
  }, []);

  // ─── filter-change handlers — use Grid API state (not ref) ─────────────────
  const onMainFilterChanged = useCallback(() => {
    if (!mainGridApi) return;
    const visibleRows = [];
    mainGridApi.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeAndPinMain(visibleRows);
  }, [mainGridApi, computeAndPinMain]);

  const onDialogFilterChanged = useCallback(() => {
    if (!dialogGridApi) return;
    const visibleRows = [];
    dialogGridApi.forEachNodeAfterFilter((node) => {
      if (node.data) visibleRows.push(node.data);
    });
    computeAndPinDialog(visibleRows);
  }, [dialogGridApi, computeAndPinDialog]);

  // ─── footer row style ───────────────────────────────────────────────────────
  const getRowStyle = (params) => {
    if (params.node.rowPinned === "bottom") {
      return {
        background: "#E59400",
        color: "#ffffff",
        fontWeight: "800",
        fontSize: "13px",
        borderTop: "2px solid #E59400",
      };
    }
    return null;
  };

  // ─── fetch helpers ──────────────────────────────────────────────────────────
  const fetchEntriesByMonthId = async () => {
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
      setLoading(true);
      try {
        const options = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        };
        const response = await fetch(
          `${URLs.dsaWiseBusinessByDateRange}startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
          options
        );
        const data = await response.json();
        if (response.ok) {
          setServicesData(data);
          computeAndPinMain(data);
        } else {
          throw new Error("Failed to fetch entries data");
        }
      } catch (error) {
        showAlert(`Failed to fetch data: ${error.message}`, "error");
      } finally {
        setLoading(false);
      }
    } else {
      showAlert(`Please select both start and end dates`, "error");
    }
  };

  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClickOpenDailog = () => setOpenDailog(true);
  const handleCloseDailog = () => setOpenDailog(false);
  const handleCloseAlert = () => setOpen(false);

  const handleViewClick = async (rowData) => {
    const dsaId = rowData.dsaId;
    setFinanceName(rowData.dsaName);
    handleClickOpenDailog();
    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");
      setLoading(true);
      try {
        const options = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt_token}`,
          },
        };
        const url = `${URLs.dsaWiseBusinessEntriesByDateRange}dsaId=${dsaId}&startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
        const response = await fetch(url, options);
        const data = await response.json();
        if (response.ok) {
          setProviderBusinessData(data);
          computeAndPinDialog(data);
        } else {
          throw new Error("Failed to fetch entries data");
        }
      } catch (error) {
        showAlert(`Failed to fetch data: ${error.message}`, "error");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (monthIdData && monthIdData.length > 0) {
      setMonthId(monthIdData[0].businessMonthID);
      fetchEntriesByMonthId();
    }
  }, [monthIdData]);

  // ─── auto-width helper ──────────────────────────────────────────────────────
  const applyAutoWidth = (ws, sheetRows, headers) => {
    const colWidths = headers.map((h) => {
      const maxDataLen = sheetRows.reduce((max, row) => {
        const val = row[h] !== undefined && row[h] !== null ? String(row[h]) : "";
        return Math.max(max, val.length);
      }, 0);
      return { wch: Math.min(60, Math.max(10, h.length, maxDataLen)) };
    });
    ws["!cols"] = colWidths;
  };

  // ─── Main grid download — uses mainGridApi (not ref) ───────────────────────
  const handleDownload = () => {
    if (!mainGridApi) return;

    const exportRows = [];
    mainGridApi.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) exportRows.push(node.data);
    });
    if (!exportRows.length) return;

    const sum = (field) => exportRows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    const totalsRow = {
      dsaName: "Total",
      totalDsaBusiness: sum("totalDsaBusiness"),
      totalGrossPayouts: sum("totalGrossPayouts"),
      totalFinalGrossPayouts: sum("totalFinalGrossPayouts"),
      totalDsaPayoutAmount: sum("totalDsaPayoutAmount"),
      totalFinalDsaPayoutsAmount: sum("totalFinalDsaPayoutsAmount"),
      totalSubDsaPayoutAmount: sum("totalSubDsaPayoutAmount"),
      totalFinalSubDsaPayoutsAmount: sum("totalFinalSubDsaPayoutsAmount"),
      dsaRevenue: sum("dsaRevenue"),
    };

    const exportColumns = columns.filter((c) => c.field);
    const headers = exportColumns.map((c) => c.headerName);
    const sheetRows = [...exportRows, totalsRow].map((row) => {
      const obj = {};
      exportColumns.forEach((col) => {
        obj[col.headerName] = row[col.field] ?? "";
      });
      return obj;
    });

    const ws = utils.json_to_sheet(sheetRows, { header: headers });
    applyAutoWidth(ws, sheetRows, headers);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "DSA Business Data");
    writeFile(wb, "Monthly_DSA_Business_Data.xlsx");
  };

  // ─── Dialog grid download — uses dialogGridApi (not ref) ───────────────────
  const handleDialogDownload = () => {
    if (!dialogGridApi) return;

    const exportRows = [];
    dialogGridApi.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) exportRows.push(node.data);
    });
    if (!exportRows.length) return;

    const sum = (field) => exportRows.reduce((s, r) => s + (parseFloat(r[field]) || 0), 0);
    const totalsRow = {
      customerName: "Total",
      loanAmount: sum("loanAmount"),
      revenue: sum("revenue"),
      dsaPayoutValue: sum("dsaPayoutValue"),
      companyRetention: sum("companyRetention"),
      finalConnectorPayoutAmount: sum("finalConnectorPayoutAmount"),
    };

    const exportColumns = providerBusinessColumns.filter((c) => c.field);
    const headers = exportColumns.map((c) => c.headerName);
    const sheetRows = [...exportRows, totalsRow].map((row) => {
      const obj = {};
      exportColumns.forEach((col) => {
        obj[col.headerName] = row[col.field] ?? "";
      });
      return obj;
    });

    const ws = utils.json_to_sheet(sheetRows, { header: headers });
    applyAutoWidth(ws, sheetRows, headers);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "DSA Entries");
    writeFile(wb, `${financeName}_Business_Data.xlsx`);
  };

  // ─── View cell renderer ─────────────────────────────────────────────────────
  const ActionCellRendererProviderBusiness = (props) => {
    if (props.node.rowPinned) return null;

    const rowIndex = props.node.rowIndex;
    const rowData = props.api.getDisplayedRowAtIndex(rowIndex)?.data;
    if (!rowData) return null;

    return (
      <ViewButton type="button" onClick={() => handleViewClick(rowData)}>
        <VisibilityIcon />
      </ViewButton>
    );
  };
  ActionCellRendererProviderBusiness.propTypes = {
    node: PropTypes.shape({
      rowIndex: PropTypes.number,
      rowPinned: PropTypes.string,
    }).isRequired,
    api: PropTypes.shape({
      getDisplayedRowAtIndex: PropTypes.func.isRequired,
    }).isRequired,
  };

  // ─── INR formatter ──────────────────────────────────────────────────────────
  const fmtINR = (val) => {
    const n = parseFloat(val);
    return isNaN(n)
      ? "₹ 0.00"
      : `₹ ${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ─── Column definitions ─────────────────────────────────────────────────────
  const columns = [
    {
      headerName: "DSA Name",
      field: "dsaName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
    {
      headerName: "Total Business",
      field: "totalDsaBusiness",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Total Gross Payouts",
      field: "totalGrossPayouts",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Final Gross Payouts",
      field: "totalFinalGrossPayouts",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DSA Payout Amount",
      field: "totalDsaPayoutAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Final DSA Payouts Amount",
      field: "totalFinalDsaPayoutsAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Sub DSA Payout Amount",
      field: "totalSubDsaPayoutAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Final Sub DSA Payouts Amount",
      field: "totalFinalSubDsaPayoutsAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DSA Revenue",
      field: "dsaRevenue",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "View",
      cellRenderer: ActionCellRendererProviderBusiness,
      width: 80,
    },
  ];

  const providerBusinessColumns = [
    {
      headerName: "Application Number",
      field: "applicationNumber",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "Loan Type",
      field: "loanType",
      filter: true,
      sortable: true,
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: "Status",
      field: "status",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
    },
    {
      headerName: "Customer Name",
      field: "customerName",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1.5,
    },
    {
      headerName: "Loan Amount",
      field: "loanAmount",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Source",
      field: "source",
      filter: true,
      sortable: true,
      minWidth: 120,
      flex: 1,
    },
    {
      headerName: "Revenue (%)",
      field: "revenuePercentage",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) =>
        p.value !== undefined && p.value !== null ? `${p.value.toFixed(2)}%` : "0",
    },
    {
      headerName: "Revenue",
      field: "revenue",
      filter: true,
      sortable: true,
      minWidth: 230,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DSA Pay-out (%)",
      field: "dsaPayoutPercentage",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) =>
        p.value !== undefined && p.value !== null ? `${p.value.toFixed(2)}%` : "0",
    },
    {
      headerName: "DSA Pay-out Value",
      field: "dsaPayoutValue",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Company Retention",
      field: "companyRetention",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "Retention (%)",
      field: "retentionPercentage",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1.5,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) =>
        p.value !== undefined && p.value !== null ? `${p.value.toFixed(2)}%` : "0",
    },
    {
      headerName: "Final Connector Payout",
      field: "finalConnectorPayoutAmount",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (p) => fmtINR(p.value),
    },
    {
      headerName: "DSA Name",
      field: "dsaName",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1,
    },
    {
      headerName: "Sub Manager",
      field: "subManager",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "BM/CH Name",
      field: "bmChName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      headerName: "EMP ID",
      field: "employeeCode",
      filter: true,
      sortable: true,
      minWidth: 150,
      flex: 1,
    },
    {
      headerName: "EMP NAME",
      field: "employeeName",
      filter: true,
      sortable: true,
      minWidth: 170,
      flex: 1.5,
    },
    {
      headerName: "ProviderName",
      field: "providerName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
    {
      headerName: "BANK NAME",
      field: "bankName",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 2,
    },
  ];

  useEffect(() => {
    setRowData(providerBusinessData);
    computeAndPinDialog(providerBusinessData);
  }, [providerBusinessData]);

  const triggerApiCall = () => {
    businessCodeRef.current = monthId;
    fetchEntriesByMonthId();
  };

  return (
    <>
      {/* Loading dialog */}
      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog
        open={openDailog}
        onClose={handleCloseDailog}
        fullScreen
        sx={{
          ".MuiDialog-paper": {
            width: "90%",
            height: "90%",
            maxWidth: "none",
            minWidth: "500px",
            margin: "auto",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle>
          <StyledDialogBoxTitle>{financeName} Business</StyledDialogBoxTitle>
          <StyledButton
            onClick={handleDialogDownload}
            sx={{ position: "absolute", right: 90, top: 8 }}
          >
            <DownloadForOfflineIcon fontSize="medium" />
          </StyledButton>
          <StyledButton
            edge="end"
            color="inherit"
            onClick={handleCloseDailog}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            Close
          </StyledButton>
        </DialogTitle>
        <DialogContent>
          <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
            <AgGridReact
              ref={dialogGridRef}
              onGridReady={(params) => setDialogGridApi(params.api)}
              rowData={providerBusinessData}
              columnDefs={providerBusinessColumns}
              pagination={true}
              paginationPageSize={10}
              pinnedBottomRowData={dialogFooter}
              getRowStyle={getRowStyle}
              onFilterChanged={onDialogFilterChanged}
              animateRows={true}
            />
          </StyledAgGridContainer>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleCloseAlert} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>

      {/* Main table */}
      <TableContainer component={Paper} sx={monthEndStyles.tableContainer}>
        <Box sx={monthEndStyles.formContainer}>
          <Box sx={monthEndStyles.formItem}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              sx={{ ...MInput.b, maxWidth: "140px" }}
              InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              sx={{ ...MInput.b, maxWidth: "140px" }}
              InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
            />
            <StyledButton onClick={triggerApiCall}>Get</StyledButton>
          </Box>

          <StyledButton onClick={handleDownload}>
            <DownloadForOfflineIcon fontSize="medium" />
          </StyledButton>
        </Box>

        <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
          <AgGridReact
            ref={mainGridRef}
            onGridReady={(params) => setMainGridApi(params.api)}
            rowData={servicesData}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={10}
            pinnedBottomRowData={footerData}
            getRowStyle={getRowStyle}
            onFilterChanged={onMainFilterChanged}
          />
        </StyledAgGridContainer>
      </TableContainer>
    </>
  );
}
